import type { Language } from '@nxt-insurance/constants'
import { SupportedMarketKey } from '@nxt-insurance/constants'

import { quote } from '@flows/configs/de/childSaving/steps/quote'
import { sustainability } from '@flows/configs/de/childSaving/steps/sustainability'
import { getFlowConfiguration } from '@flows/configs/shared/flowConfiguration'
import {
  appointmentDetailsSchema,
  appointment_details,
} from '@flows/configs/shared/mgaLight/steps/appointmentDetails'
import { getLoginSchema, login } from '@flows/configs/shared/mgaLight/steps/login'
import { phoneNumberConfirmation } from '@flows/configs/shared/mgaLight/steps/phoneConfirmation'
import { progressFirstStep } from '@flows/configs/shared/mgaLight/steps/progressFirstStep'
import type { AnySteps } from '@flows/core/types/stepTypes'
import { generateGetFlowValueFunctions } from '@flows/insurance/utils'

import { amountOfMoney } from './steps/amountOfMoney'
import { numberOfChildren } from './steps/numberOfChildren'
import { progressLastStep } from './steps/progressLastStep'
import { success } from './steps/success'
import { childSavingSchema } from './types'

const helperFunctions = generateGetFlowValueFunctions('mga_child_saving_de')
const schema = childSavingSchema.merge(getLoginSchema()).merge(appointmentDetailsSchema)

export const getChildSavingConfig = (args: {
  getCurrentLanguage: () => Language
  platform: 'web' | 'native'
  environment: string
}) => {
  const params = {
    helperFunctions,
    schema,
    getCurrentLanguage: args.getCurrentLanguage,
    platform: args.platform,
    environment: args.environment,
  }
  const steps = {
    // TODO: This step is temporarily not used. But we don't plan to completely remove it, therefore the code should remain here
    // This will be re-checked in the scope of this ticket: https://hellogetsafe.atlassian.net/browse/IA-540
    progress_first_step: progressFirstStep({
      platform: args.platform,
      nextStep: 'number_of_children',
    }),
    number_of_children: numberOfChildren(params),
    amount_of_money: amountOfMoney(params),
    sustainability: sustainability(params),
    quote: quote(params),
    login: login({ market: SupportedMarketKey.De, platform: args.platform }),
    phone_confirmation: phoneNumberConfirmation({ market: SupportedMarketKey.De }),
    progress_last_step: progressLastStep(params),
    success: success(params),
    appointment_details: appointment_details({
      type: 'translatableText',
      translationKey: 'sharedFlows_childSaving:title',
    }),
  } as const satisfies AnySteps
  return getFlowConfiguration(steps, {
    constants: {
      flowName: 'child-saving',
      SCHEMA: schema,
      FIRST_STEP_NAME: 'number_of_children',
      productKey: 'mga_child_saving_de',
    },
  })
}
